import type { WidgetName } from './form-template-document.types';

/**
 * The `yes-no-unknown` widget's fixed vocabulary — its options are not authored,
 * so the values live with the widget's other contract rather than in a document.
 * Lowercase kebab like every other value literal in the language; the localised
 * labels are the renderers' concern.
 */
export const YES_NO_UNKNOWN_VALUES = ['yes', 'no', 'unknown'] as const;
export type YesNoUnknownValue = (typeof YES_NO_UNKNOWN_VALUES)[number];

/**
 * The `rating` widget's fixed vocabulary — a two-way judgement of something the
 * form asks the user to rate. Widget-owned rather than authored, so no template
 * can offer a third grade a renderer has no control for.
 *
 * Declared here rather than taken from the legacy `DermloopRatingEnum` it
 * happens to agree with: a value literal in the language answers to the
 * language's own casing rule, and the widget stores its grade in the submission
 * blob, so there is no column whose enum this could drift from.
 */
export const RATING_VALUES = ['good', 'bad'] as const;
export type RatingValue = (typeof RATING_VALUES)[number];

export interface WidgetSlotSpec {
  /** Input slots that must be bound when the field has a bind. */
  readonly required: readonly string[];
  /** Input slots that may be bound. */
  readonly optional: readonly string[];
  /** Values the widget emits under its own field id in `outputs.*`. */
  readonly outputs: readonly string[];
}

/** Keyed by field id, so repeated placements of one widget never overwrite each other's outputs. */
export function fieldOutputPath(fieldId: string, output: string): string {
  return `outputs.${fieldId}.${output}`;
}

/**
 * The per-widget slot contract, driving bind validation
 * (`validateFormTemplateDocument`) and the submission-schema generator
 * (`generateSubmissionSchema`).
 */
export const WIDGET_SLOTS: Record<WidgetName, WidgetSlotSpec> = {
  'yes-no-unknown': { required: ['value'], optional: [], outputs: [] },
  'single-select': { required: ['selected'], optional: ['other'], outputs: [] },
  'multi-select': { required: ['selected'], optional: ['other'], outputs: [] },
  text: { required: ['value'], optional: [], outputs: [] },
  note: { required: ['value'], optional: [], outputs: [] },
  'skin-type': { required: ['selected'], optional: [], outputs: [] },
  number: { required: ['value'], optional: [], outputs: [] },
  'diagnosis-tree': {
    required: ['selected'],
    optional: ['other'],
    outputs: ['isMelanoma'],
  },
  'diagnosis-search': {
    required: ['selected'],
    optional: ['other'],
    outputs: ['isMelanoma'],
  },
  'clinical-action': { required: ['selected'], optional: [], outputs: [] },
  scale: { required: ['selected'], optional: [], outputs: [] },
  'image-capture': { required: ['images'], optional: [], outputs: [] },
  'location-picker': { required: ['coordinate'], optional: [], outputs: [] },
  rating: { required: ['selected'], optional: [], outputs: [] },
};
